<template>
  <div
    class="py-0 pb-4"
    style="width: 100%"
  >
    <v-card
      class="pb-5"
      style="min-height: 244px"
    >
      <!-- add attachment header -->
      <v-card-title
        v-if="!isFileSelected"
        class="pb-5"
      >
        Attachments
        <v-spacer></v-spacer>

        <!-- select attachment -->
        <btn
          label="Select Attachment"

          :disabled="signed && dialogAttachmentType === 'images'"
          @click="selectAttachmentClicked"
        ></btn>
        <input
          ref="uploader"
          type="file"
          class="d-none"
          accept="image/*, application/pdf"
          @change="onFileChanged"
        />
      </v-card-title>

      <!-- upload attachment header -->
      <v-form
        v-else
        ref="uploadForm"
        v-model="formValid"
        @submit.prevent="addFile"
      >
        <v-card-title class="pb-5">
          <!-- cancel -->
          <btn
            label="Cancel"
            :icon="icons.mdiClose"
            color="secondary"
            @click="cancelUpload(false)"
          ></btn>

          <v-spacer></v-spacer>

          <!-- name file -->
          <text-field
            v-model="fileDescription"
            label="Attachment Description"
            required
            maxlength="120"
            hint="The following characters are not allowed: / \ : * ? &quot; < > |"
            style="margin-bottom: -25px"
            @blur="normalizeFilename"
          ></text-field>

          <v-spacer></v-spacer>

          <img
            v-if="isImageType(attachment.attachment_mime_type)"
            :src="imagePreview"
            class="image-preview"
          >
          <div
            v-else
            class="image-preview mime-type"
          >
            {{ mimeType(attachment.attachment_mime_type) }}
          </div>

          <v-spacer></v-spacer>

          <!-- add file -->
          <btn
            label="Add File"
            :disabled="!formValid"
            :icon="icons.mdiFileImagePlus"
            :loading="isAddingFile"
            @click="addFile"
          ></btn>
        </v-card-title>
      </v-form>

      <!-- attachment type list -->
      <v-card-text>
        <v-btn-toggle
          v-model="dialogAttachmentTypeId"
          class="d-flex align-center justify-center"
        >
          <btn
            v-for="bucket in buckets"
            :key="bucket.value"
            :disabled="isFileSelected"
            :label="bucket.title"
            :icon="bucket.icon"
            :value="bucket.value"
            :style="`width:${100 / buckets.length}%; ${buckets.length === 1 ? 'pointer-events:none;' : ''}`"
            :color="dialogAttachmentType === bucket.value ? 'success' : '#ddd'"
            @click="attachmentTypeClicked(bucket)"
          >
            <v-badge
              v-if="attachmentCount(bucket.value)"
              color="success"
              :content="attachmentCount(bucket.value)"
              inline
            >
              {{ bucket.title }}
            </v-badge>
          </btn>
        </v-btn-toggle>
      </v-card-text>
      <div class="note-section col-sm-12">
        <v-expand-transition>
          <chip
            v-if="hasTreatment"
            color="primary"
            :icon="icons.mdiInformationOutline"
            :label="true"
            icon-color="white"
            class="mb-2 x-large full-width-chip text-center"
          >
            {{ computedAttachmentLabel }}
          </chip>
        </v-expand-transition>
      </div>
      <div style="max-height: 205px; overflow-y: auto;">
        <v-data-table
          :headers="headers"
          :items="currentAttachmentsArr()"
          :class="isFileSelected && 'disable'"
          item-key="id"
          sort-by="created"
          :sort-desc="true"
          disable-pagination
          hide-default-footer
          @click:row="viewAttachment"
        >
          <!-- image -->
          <template #item.data="{ item }">
            <v-img
              v-if="isImageType(item.attachment_mime_type) && item.data !== 'placeholder'"
              :lazy-src="item.data"
              :src="item.data"
              max-width="250"
              max-height="150"
            ></v-img>
            <pdf
              v-else-if="isPDFType(item.attachment_mime_type) && item.data !== 'placeholder'"
              :src="item.data"
              :page="1"
              class="pdf-preview"
            ></pdf>
            <btn
              v-else-if="item.data === 'placeholder'"
              label="Click to view attachment"
              color="primary"
              offline-disable
            ></btn>
            <!--
            <div
              v-else-if="isPDFType(item.attachment_mime_type)"
              class="table-preview"
            >
              <v-icon x-large>
                {{ icons.mdiFilePdfBox }}
              </v-icon>
            </div>
            -->
            <div
              v-else
              class="table-preview"
            >
              {{ mimeType(item.attachment_mime_type) }}
            </div>
          </template>

          <!-- created date/time -->
          <template #item.created="{ item }">
            {{ formatDateTime(item.created) }}
          </template>

          <!-- size -->
          <template #item.size="{ item }">
            {{ Math.round(item.data.length / 1024) }}KB
          </template>

          <!-- delete -->
          <template #item.updated="{ item }">
            <v-icon
              v-if="item.updated"
              right
              color="error"
              @click.stop="deleteAttachment(null, item)"
            >
              {{ icons.mdiTrashCan }}
            </v-icon>
            <v-icon
              v-else
              right
              color="success"
              @click.stop="viewAttachment(item)"
            >
              {{ icons.mdiCloudCheckVariant }}
            </v-icon>
          </template>
        </v-data-table>
      </div>
    </v-card>

    <v-overlay
      v-if="showOverlay"
      rem:value="showOverlay"
    >
      <v-card
        class="attachment-overlay"
      >
        <v-img
          v-if="isImage"
          :src="currentAttachmentsArr()[imageViewIndex].data"
          contain
          @error="handleURLExpireError()"
        >
          <template #placeholder>
            <v-row
              class="fill-height ma-0"
              align="center"
              justify="center"
              style="flex-direction: column"
            >
              <v-progress-circular
                indeterminate
                size="64"
                width="7"
                color="primary"
                class="load-sync-progress"
              ></v-progress-circular>
              <p
                class="loading-message"
                style="color: var(--v-accent-lighten3)"
              >
                Loading...
              </p>
            </v-row>
          </template>
        </v-img>
        <v-row
          v-if="isPDF && loadingPDF"
          class="fill-height ma-0"
          align="center"
          justify="center"
          style="flex-direction: column"
        >
          <v-progress-circular
            indeterminate
            size="64"
            width="7"
            color="primary"
            class="load-sync-progress"
          ></v-progress-circular>
          <p
            class="loading-message"
            style="color: var(--v-accent-lighten3)"
          >
            Loading...
          </p>
        </v-row>
        <div v-if="isPDF">
          <pdf
            v-for="i in pagesPDF"
            :key="i"
            :src="srcPDF"
            :page="i"
            class="pdf-viewer"
            @loaded="pageLoaded(i)"
            @error="handleURLExpireError()"
          ></pdf>
        </div>
        <v-row
          align="center"
          justify="space-between"
          class="controls"
        >
          <btn
            label="Close"
            color="secondary"
            :icon="icons.mdiClose"
            @click="closeOverlay"
          ></btn>
          <div class="attachment-title">
            {{ imageTitle }}
          </div>
          <div>
            {{ formatDateTime(imageDate) }}
          </div>
          <btn
            v-if="currentAttachmentsArr()[imageViewIndex].updated"
            label="Delete"
            :icon="icons.mdiTrashCan"
            color="error"
            @click="deleteAttachment(imageViewIndex)"
          ></btn>
          <div
            v-if="currentAttachmentsArr()[imageViewIndex].updated !== true"
            class="synced"
          >
            <v-icon
              color="success"
              class="mr-1"
            >
              {{ icons.mdiCloudCheckVariant }}
            </v-icon>
            Synced
          </div>
        </v-row>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */

import {
  mdiClose,
  mdiCloudCheckVariant,
  mdiDownload,
  mdiFileImagePlus,
  mdiFilePdfBox,
  mdiTrashCan,
  mdiInformationOutline,
} from '@mdi/js'
import pdf from '@teckel/vue-pdf'
import { heicTo, isHeic } from 'heic-to'
import { v4 as uuidv4 } from 'uuid'
import Vue from 'vue'
import { mapGetters } from 'vuex'

export default {
  components: { pdf },
  props: {
    attachmentTypeId: {
      type: [Number, String],
      default: null,
    },
    attachmentType: {
      type: String,
      default: null,
    },
    signed: {
      type: Boolean,
      default: false,
    },
    patientId: {
      type: String,
      default: null,
    },
    patientAttachments: {
      type: Array,
      default: null,
    },
    defaultAttachmentType: {
      type: String,
      default: null,
    },
    providedBuckets: {
      type: Array,
      default: () => [],
    },
    treatmentId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      srcPDF: null,
      pagesPDF: 1,
      loadingPDF: true,
      formValid: false,
      showOverlay: false,
      isImage: false,
      isPDF: false,
      imageViewIndex: null,
      imageTitle: null,
      imageDate: null,
      dialogAttachmentCreate: false,
      dialogAttachmentTypeId: null,
      dialogAttachmentType: null,
      encounterId: this.$route.query.id,
      isFileSelected: false,
      isAddingFile: false,
      selectedFile: null,
      file: [],
      attachment: {
        attachment_filename: null,
        attachment_mime_type: null,
        attachment_filepath: '',
        attachment_width: 0,
        attachment_height: 0,
        data: null,
      },
      downloadedAttachments: [],
      fileDescription: null,
      imagePreview: null,
      icons: {
        mdiClose, mdiFileImagePlus, mdiTrashCan, mdiCloudCheckVariant, mdiFilePdfBox, mdiDownload, mdiInformationOutline,
      },
      headers: [
        {
          text: 'Image',
          value: 'data',
          align: 'center',
          sortable: false,
          width: 250,
          filter: value => !this.data || value === this.data,
        },
        {
          text: 'Description',
          value: 'attachment_filename',
          align: 'center',
          width: 200,
        },
        {
          text: 'Created',
          value: 'created',
          align: 'center',
        },
        {
          text: 'Size',
          value: 'size',
          sortable: false,
        },
        {
          text: 'Synced',
          value: 'updated',
          align: 'small',
        },
        {
          text: 'Bucket',
          value: 'attachment_type',
          align: 'hide',
          filter: value => !this.dialogAttachmentType || value === this.dialogAttachmentType,
        },
      ],
    }
  },
  computed: {
    ...mapGetters('baseData', ['amnioticBucketTypes']),
    ...mapGetters(['isOnline']),
    buckets() {
      if (this.providedBuckets && this.providedBuckets.length) {
        return this.providedBuckets
      }

      return this.amnioticBucketTypes
    },
    hasTreatment() {
      // This determines if the attachment will or will not have a treatment_id
      // It also helps to filter the current treatment attachments
      return ['amnio-packaging', 'amnio-meas', 'amnio-graft'].includes(this.dialogAttachmentType)
    },
    computedAttachmentLabel() {
      if (this.dialogAttachmentType === 'amnio-meas') {
        return 'Wound bed with measuring tape visible to show wound dimensions'
      }

      if (this.dialogAttachmentType === 'amnio-graft') {
        return 'Wound with amniotic tissue graft product applied'
      }

      if (this.dialogAttachmentType === 'amnio-packaging') {
        return 'Amniotic tissue graft product packaging with brand and serial number information visible'
      }

      return ''
    },
  },
  watch: {
    attachmentType() {
      this.dialogAttachmentType = this.attachmentType
    },
    showOverlay() {
      this.loadingPDF = true
      if (this.showOverlay && this.isPDF) {
        this.srcPDF = pdf.createLoadingTask(this.currentAttachmentsArr()[this.imageViewIndex]?.data)
        this.srcPDF.promise.then(file => {
          this.pagesPDF = file.numPages
        }).catch(e => console.error(e))
      }
    },
    patientAttachments: {
      deep: true,
      handler() {
        if (this.downloadedAttachments.length > 0) {
          // Add any locally created attachments
          this.patientAttachments.forEach(attachment => {
            if (attachment.updated && !this.downloadedAttachments.find(e => e.id === attachment.id)) {
              this.downloadedAttachments.push(attachment)
            }
          })

          // Remove attachment from downloadedAttachments if removed from patientAttachments
          this.downloadedAttachments.forEach((attachment, attachmentIndex) => {
            const index = this.patientAttachments.findIndex(e => e.id === attachment.id)

            // Only check locally created attachments via the 'updated' property.
            if (attachment.updated && index === -1) {
              this.downloadedAttachments.splice(attachmentIndex, 1)
            }
          })
        }
      },
    },
  },
  mounted() {
    this.dialogAttachmentTypeId = Number(this.attachmentTypeId)
    this.dialogAttachmentType = this.defaultAttachmentType
  },
  methods: {
    normalizeFilename() {
      // Removes non-ASCII characters
      this.fileDescription = this.fileDescription
        .replace(/[^\x00-\x7F]/g, '')        // remove non-ASCII
        .replace(/\//g, '-')                // replace / with -
        .replace(/[\\:*?"<>|]/g, '')       // remove \ : * ? " < > |
        .substring(0, 120)                // limit to 120 characters
        .trim()
    },
    attachmentTypeClicked(bucket) {
      this.dialogAttachmentType = bucket.value
    },
    pageLoaded(page) {
      if (page === this.pagesPDF) {
        setTimeout(() => { // Gives it a second to render, reduces flashing
          const pages = document.querySelectorAll('.pdf-viewer')
          pages.forEach(el => {
            el.style.height = 'auto'
            el.style.opacity = '1'
          })
          this.loadingPDF = false
        }, 1000)
      }
    },
    formatDate(dateTime) {
      return this.$date(dateTime).format('M/D/YYYY')
    },
    formatDateTime(dateTime) {
      return this.$date(dateTime).format('M/D/YYYY h:mm:ss A')
    },
    attachmentCount(bucket) {
      if (!Array.isArray(this.patientAttachments)) return false

      if (this.hasTreatment) {
        return this.patientAttachments
          .filter(a => a.treatment_id === this.treatmentId)
          .reduce((count, item) => count + (item.attachment_type === bucket), 0)
      }

      return this.patientAttachments.reduce((count, item) => count + (item.attachment_type === bucket), 0)
    },
    isPDFType(mime) {
      return mime === 'application/pdf'
    },
    isImageType(mime) {
      return mime.substring(0, 5) === 'image'
    },
    mimeType(mime) {
      return mime.substring(mime.indexOf('/') + 1).toUpperCase()
    },
    viewAttachment(item) {
      if (item.data === 'placeholder') {
        if (this.isOnline) {
          try {
            this.downloadAttachment(item)
          } catch (e) {
            this.$store.dispatch('notify', { value: `Issue with retrieving attachment URL parameters. ${e}`, color: 'error' })
          }
        } else {
          this.$store.dispatch('notify', { value: 'Please connect to the internet to view attachment.', color: 'error' })
        }
      } else if (this.isImageType(item.attachment_mime_type) || this.isPDFType(item.attachment_mime_type)) {
        this.imageViewIndex = this.currentAttachmentsArr().findIndex(x => x.id === item.id)
        this.imageTitle = item.attachment_filename
        this.imageDate = item.created
        // eslint-disable-next-line no-unused-expressions
        this.isImageType(item.attachment_mime_type) ? this.isImage = true : this.isPDF = true
        this.showOverlay = true
      } else {
        this.$store.dispatch('notify', { value: `Attachment must be an image or PDF to view, file type is: ${this.mimeType(item.attachment_mime_type)}` })
        this.closeOverlay()
      }
    },
    closeOverlay() {
      this.showOverlay = false
      this.imageViewIndex = null
      this.imageTitle = null
      this.imageDate = null
      this.isImage = false
      this.isPDF = false
    },
    deleteAttachment(index, item = null) {
      this.$root.confirm({
        title: 'Delete Attachment?',
        body: 'Are you sure you wish to delete this attachment?',
        cancel: 'No',
        confirm: 'Delete',
        confirmIcon: this.icons.mdiTrashCan,
        confirmColor: 'error',
      }).then(result => {
        if (result) {
          this.closeOverlay()
          // eslint-disable-next-line no-param-reassign
          if (index === null) index = this.patientAttachments.findIndex(x => x.id === item.id)
          if (index !== -1) {
            this.patientAttachments.splice(index, 1)
          }
          this.updateAttachments()
        }
      })
    },
    selectAttachmentClicked() {
      this.$refs.uploader.click()
    },
    async testHEIC(file) {
      // eslint-disable-next-line no-return-await
      return await isHeic(file)
    },
    async convertHEIC(file) {
      return heicTo({
        blob: file,
        type: 'image/jpeg',
        quality: 0.5,
      })
    },
    async onFileChanged(e) {
      // validate selected file
      if (e.target.files.length > 0) {
        const selectedFile = e.target.files[0]
        const allowPDF = this.$store.getters['baseData/amnioticBucketTypeFromValue'](this.dialogAttachmentType).pdf
        let processedFile = selectedFile

        const isHEICFile = await isHeic(selectedFile)
        if (isHEICFile) {
          try {
            const convertedBlob = await this.convertHEIC(selectedFile)
            processedFile = new File([convertedBlob], selectedFile.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' })
          } catch (error) {
            this.$refs.uploader.value = null
            this.resetUploadValues()
            await this.$store.dispatch('notify', {
              value: 'Error converting HEIC/HEIF file. Please try a different image format.',
              color: 'error',
            })

            return
          }
        }

        if (this.isImageType(processedFile?.type) || (allowPDF && this.isPDFType(processedFile?.type))) {
          this.file = processedFile
          this.fileDescription = this.file.name.split('.').slice(0, -1).join('.')
          this.normalizeFilename()
          if (this.fileDescription === 'image') {
            this.fileDescription = `${this.$custom.toProperCase(this.dialogAttachmentType.slice(0, -1))} ${this.formatDate(Date.now())}`
          }
          this.imagePreview = URL.createObjectURL(this.file)
          this.attachment = {
            ...this.attachment,
            encounter_id: this.encounterId,
            patient_id: this.patientId,
            attachment_type: this.dialogAttachmentType,
            attachment_mime_type: this.file.type,
            attachment_filename: this.fileDescription,
            attachment_filesize: this.file.size,
            data: this.imagePreview,
          }
          this.isFileSelected = true
        } else {
          this.$refs.uploader.value = null
          this.resetUploadValues()
          await this.$store.dispatch('notify', {
            value: `Attachment must be an image${allowPDF ? ' or PDF' : ''}, file type is: ${this.mimeType(selectedFile.type)}`,
            color: 'error',
          })
        }
      }
    },
    addFile() {
      this.normalizeFilename()
      if (!this.fileDescription) return

      this.isAddingFile = true

      const params = {
        attachment: {
          ...this.attachment,
          id: uuidv4(), // generate unique ID for the attachment
          updated: true,
          created: this.$custom.utcNow(), // current UTC timestamp
          attachment_filename: this.fileDescription
        },
        file: this.file
      }

      if (this.hasTreatment) {
        params.attachment.treatment_id = this.treatmentId
      }

      const reader = new FileReader()
      reader.readAsDataURL(params.file) // read file as DataURL for image processing or direct upload

      reader.onload = () => {
        // Check if the file is an image type
        if (this.isImageType(params.attachment.attachment_mime_type)) {
          const img = document.createElement('img')
          img.src = reader.result // load image data

          img.onload = () => {
            // Determine if file is small enough to skip resizing
            const isSmallFile = params.file.size <= 384000 // 384 KB threshold
            // Set JPEG quality: 85% for small files, 70% for large files to reduce size
            const quality = isSmallFile ? 0.85 : 0.7

            // Default dimensions to original image size
            let newWidth = img.width
            let newHeight = img.height

            // Resize only if file is large
            if (!isSmallFile) {
              const maxDimension = this.dialogAttachmentType === 'amnio-packaging' ? 2500 : 1200
              const ratio = img.width / img.height
              // Scale dimensions proportionally, constraining the largest side to maxDimension
              if (img.width > img.height) {
                newWidth = maxDimension
                newHeight = maxDimension / ratio
              } else {
                newHeight = maxDimension
                newWidth = maxDimension * ratio
              }
            }

            // Create canvas for resizing and JPEG conversion
            const canvas = document.createElement('canvas')
            canvas.width = newWidth
            canvas.height = newHeight

            const ctx = canvas.getContext('2d')
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'

            // Draw the image to canvas with calculated dimensions
            ctx.drawImage(img, 0, 0, newWidth, newHeight)

            // Convert canvas image to JPEG data URL with specified quality
            const jpegData = canvas.toDataURL('image/jpeg', quality)

            // Update attachment details with new image info
            params.attachment.attachment_width = newWidth
            params.attachment.attachment_height = newHeight
            params.attachment.attachment_mime_type = 'image/jpeg'
            params.attachment.attachment_filesize = jpegData.length
            params.attachment.data = jpegData

            // Add the processed attachment and update UI/store
            this.patientAttachments.push(params.attachment)
            this.updateAttachments()
            this.$store.dispatch('notify', {
              value: 'Attachment added successfully.',
              color: 'success'
            })
            this.resetUploadValues()
          }
        } else {
          // For non-image files, just use the original DataURL without processing
          params.attachment.data = reader.result
          this.patientAttachments.push(params.attachment)
          this.updateAttachments()
          this.$store.dispatch('notify', {
            value: 'Attachment added successfully.',
            color: 'success'
          })
          this.resetUploadValues()
        }
      }
    },
    updateAttachments() {
      this.$store.commit('patients/updateAttachments', {
        id: this.patientId,
        attachments: this.patientAttachments,
      })
    },
    downloadAttachment(item) {
      const attachmentUrl = this.validateCreateUrl(item)

      this.$store.dispatch('encounters/syncingModal', !item.renewUrl)
      this.$store.dispatch('patients/getPatientAttachment', { urlString: attachmentUrl })
        .then(response => {
          if (response.status === 'success') {
            const imageData = `data:${response.data.contentType};base64,${response.data.data}`
            const attachmentIdx = this.patientAttachments.findIndex(attachment => attachment.id === item.id)
            this.patientAttachments[attachmentIdx].data = imageData

            this.$store.dispatch('encounters/syncingModal', false)
            if (!item.renewUrl) {
              this.$store.dispatch('notify', { value: 'Attachment finished downloading!', color: 'success' })
            }
          } else {
            this.$store.dispatch('encounters/syncingModal', false)
            this.$store.dispatch('notify', { value: 'Unable to download attachment', color: 'error' })
          }
        })
        .catch(e => Vue.prototype.$custom.processCommError(e, 'downloadAttachment'))
    },
    currentAttachmentsArr() {
      if (this.downloadedAttachments.length > 0) {
        return this.hasTreatment ? this.downloadedAttachments.filter(a => a.treatment_id === this.treatmentId) : this.downloadedAttachments
      }

      return this.hasTreatment ? this.patientAttachments.filter(a => a.treatment_id === this.treatmentId) : this.patientAttachments
    },
    cancelUpload(outsideModalClick) {
      this.$root.confirm({
        title: 'Cancel Adding Attachment?',
        body: 'Are you sure you wish to cancel adding the attachment?',
        cancel: 'No',
        confirm: 'Yes',
      }).then(result => {
        if (result) {
          if (outsideModalClick) {
            this.closeModal()
          } else {
            this.resetUploadValues()
          }
        }
      })
    },
    handleURLExpireError() {
      const expiredAttachment = this.currentAttachmentsArr()[this.imageViewIndex]
      expiredAttachment.data = 'placeholder'
      expiredAttachment.renewUrl = true
      this.viewAttachment(expiredAttachment)
    },
    resetUploadValues() {
      this.file = []
      this.fileDescription = null
      this.isFileSelected = false
      this.isAddingFile = false
      this.attachment = {
        attachment_filename: null,
        attachment_mime_type: null,
        attachment_filepath: '',
        attachment_width: 0,
        attachment_height: 0,
        data: null,
      }
    },
    closeModal() {
      this.modalState = false
      this.dialogAttachmentType = null
      this.$emit('update:attachmentType', this.dialogAttachmentType)
      this.resetUploadValues()
    },
    mimeTypeToExtension(mimeType) {
      const mimeTypeMap = {
        'application/pdf': '.pdf',
        'image/png': '.png',
        'image/jpg': '.jpg',
        'image/jpeg': '.jpeg',
        'image/tiff': '.tiff',
        'image/tif': '.tif',
        'image/heic': '.heic',
        'image/bmp': '.bmp',
        'image/wmf': '.wmf',
      }

      return mimeTypeMap[mimeType] || null
    },
    validateCreateUrl(attachment) {
      const { id } = attachment
      const filename = attachment.attachment_filename
      const mimeType = this.mimeTypeToExtension(attachment.attachment_mime_type)

      if (!id) {
        throw new Error('Attachment ID is missing.')
      }

      if (!filename) {
        throw new Error('Attachment filename is missing.')
      }

      if (!mimeType) {
        throw new Error(`Unknown MIME type: ${mimeType}.`)
      }

      return `${id}/${filename}${mimeType}`
    },
    clickOutside() {
      /* This causes this model to work differently than then rest of the app, so it was removed for consistency reasons
      if (this.isFileSelected) {
        this.cancelUpload(true)
      } else {
        this.closeModal()
      }
      */
    },
  },
}
</script>

<style lang="scss" scoped>
.full-width-chip {
  width: 100%;
  display: block;
  margin: 0 auto;
  font-size: 16px;
  min-height: auto !important;
  height: auto !important;
}
::v-deep .full-width-chip .v-chip__content {
  white-space: normal !important;
  word-break: break-word !important;
  overflow: visible !important;
}
.attachments-modal {
  .table-preview {
    width: 100%;
    height: 75px;
    line-height: 75px;
  }
  .pdf-preview {
    max-width: 250px;
    max-height: 150px;
  }
}
.attachment-overlay {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: map-get($shades, 'white');
  border-radius: 0;
  overflow: hidden auto;
  .v-image {
    width: 100%;
    height: 91vh;
  }
  .pdf-viewer {
    width: 100%;
    height: 100vh; // If we don't specify a height, the PDF viewer will throw errors for some reason
    opacity: 0;
    border-bottom: 10px solid black;
    display: flex !important;
    align-items: center;
    transition: opacity 0.3s ease;
    &:last-of-type {
      margin-bottom: 95px;
    }
  }
  .controls {
    background-color: map-get($shades, 'white');
    flex-wrap: nowrap;
    z-index: 50;
    position: fixed;
    bottom: 12px;
    left: 12px;
    right: 12px;
    padding: 30px;
    > div {
      color: var(--v-accent-lighten3);
      padding: 0 10px;
      max-width: 40%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      &:first-of-type {
        padding-right: 0;
      }
    }
    > .synced {
      font-size: 1rem;
      color: rgba(94, 86, 105, 0.68) !important;
    }
  }
}
::v-deep {
  .image-preview {
    height: 46px;
    line-height: 46px;
    width: auto;
    border: 1px solid #aaa;
    border-radius: 5px;
    font-size: 1rem;
    &.mime-type {
      padding: 0 5px;
    }
  }
  .v-btn.v-btn--disabled {
    &.v-btn--active {
      background-color: var(--v-secondary-lighten3) !important;
      .v-btn__content {
        color: rgba(0, 0, 0, 0.42);
      }
    }
  }
  .v-data-table.disable {
    position: relative;
    .v-data-table__wrapper:before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background-color: map-get($shades, 'white');
      z-index: 10;
      opacity: 0.5;
    }
  }
}
</style>
